import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHover } from 'components/Editor/hooks/useHover';

import { toggleEditMode, updateWidgetName } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from '../../index';

import { Frame } from 'scenejs';
import { Property } from '../../components/properties/';
import { LiveElementCreationProperty } from '../../components/liveElements';
import { HOTSPOT, PAGE } from 'constants/widgetTypes';
import { getWidgetType } from 'components/Editor/helper/widget';

import { Button } from 'react-bootstrap';
import styles from './anchor.module.scss';

const AnchorWidgetComponent = function ({ className, style, widgetProps, widgetRef }) {
  const ref = widgetRef;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const name = _.get(widgetProps, 'name');

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.anchorWidget)}
      style={style}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      <div className={styles.tag}>
        <i className={cx("cleverstory-anchor", styles.anchorIcon)} />
        <span className={styles.name}>{name}</span>
        <i className={cx("cleverstory-drag", styles.dragIcon)} />
      </div>
    </div>
  )
}

const anchorWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const AnchorWidget = React.memo(AnchorWidgetComponent, anchorWidgetEquals);

const AnchorWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
  }
  
  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Name' onClick={handleEditMode.bind(this, 'text')}>
        <i className={cx('cleverstory-text-layer', styles.textLayerIcon)} />
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {editMode &&
        <AnchorTextEditor
          id={id}
          editor={editorSession}
          widget={props}
          editMode={editMode}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

AnchorWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: false,
    rotatable: false,
    cloneable: true,
    deletable: true,
    hideDefaultLines: true,
    onReparent: (widget, parent) => {
      const type = getWidgetType(editorSession, parent)
      if (type === PAGE) return true;

      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }

      return false
    },
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');

      var y = parseInt(values[1]);
      if (['INTERACTIVE_CONTENT', 'SPATIAL'].includes(storyboardType)){
        y = Math.max(0, y);
      }

      const newTranslate = `0px, ${y}px`;

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    }
  }
}

AnchorWidget.actions = (editor, breakpoint, props) => {
  return (
    <AnchorWidgetDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
}

const AnchorTextEditor = (props) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    name: '',
    error: null
  });

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'widget.uid');
  const value = _.get(props, 'widget.name');

  useEffect(() => {
    setState(state => {
      return {
        ...state,
        name: value
      }
    })
  }, []);

  useEffect(() => {
    if (!props.editMode) {
      handleBlur();
    }
  }, [props.editMode])

  const handleChange = (e) => {
    var value = e.target.value;
    var error = null;

    if (!value || value.trim().length === 0) {
      error = "Please enter your anchor's name"
    } else {
      error = null;
    }

    setState(state => {
      return {
        ...state,
        error: error,
        name: value
      }
    })
  }

  const handleBlur = (e) => {
    if (!state.name) {
      dispatch(updateWidgetName(editorSession, id, "Anchor"));
    }
  }

  return (
    <Property.Dialog
      width={222}
      className={styles.anchorSettings}
      target={`#${id}`}
    >
      <Property.Header title={'Anchor Settings'}>
        <Button className={cx(styles.action, styles.confirm)} onClick={()=> dispatch(updateWidgetName(editorSession, id, state.name))} >
          <i className={cx("cleverstory-tick")} />
        </Button>
        <Button className={styles.action} onClick={props.onCancel}>
          <i className='cleverstory-close' />
        </Button>
      </Property.Header>
      <Property name='What do you want to call your anchor'>
        <Property.Input value={state.name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Anchor Name"
          error={state.error} />
      </Property>
    </Property.Dialog>
  );
}

export { AnchorWidget };
